import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { SectionTitle, Btn, Modal, Field, SelectField, EmptyState, Badge, SearchBar, normalize } from "@/components/Brutal";
import { openWhatsapp } from "@/lib/whatsapp";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Users as UsersIcon, MessageCircle } from "lucide-react";

const empty = { nome: "", cpf: "", telefone: "", community_id: "", tipo: "cooperado", funcao: "" };

const FUNCAO_SUGESTOES = [
  "Motosserrista", "Ajudante de Motosserrista",
  "Coordenador de Campo", "Vice Coordenador",
  "Arraste", "Romaneio", "Delimitação", "Macrozoneamento",
  "Aux. Mecânico", "Técnico Florestal", "Técnico de Enfermagem",
  "Diretoria",
  "Skidder", "Operador de Máquina",
  "Motorista", "Motorista de Caminhão", "Diarista",
  "Presidente", "Vice-Presidente", "Diretor",
  "Secretário", "Tesoureiro", "Fiscal", "Comunitário",
  "Cozinheira", "Cozinheiro(a)", "Aux. de Cozinha",
  "Auxiliar Administrativo", "Almoxarife",
  "Vigia", "Apoio", "Serviços Gerais",
  "Outro",
];

export default function Cooperados() {
  const { hasRole } = useAuth();
  const canEdit = hasRole("almoxarife");
  const [items, setItems] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tipoFilter, setTipoFilter] = useState("all");
  const [comunidadeFilter, setComunidadeFilter] = useState("");

  const load = async () => {
    setLoading(true);
    const [a, b] = await Promise.all([api.get("/cooperados"), api.get("/communities")]);
    setItems(a.data); setCommunities(b.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, community_id: form.community_id || null };
      if (editing) await api.put(`/cooperados/${editing.id}`, payload);
      else await api.post("/cooperados", payload);
      toast.success(editing ? "Atualizado" : "Criado");
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!confirm("Excluir?")) return; try { await api.delete(`/cooperados/${id}`); toast.success("Excluído"); load(); } catch (err) { toast.error(formatError(err.response?.data?.detail)); } };

  const filtered = items.filter((c) => {
    if (tipoFilter !== "all" && (c.tipo || "cooperado") !== tipoFilter) return false;
    if (comunidadeFilter && (c.community_id || "") !== comunidadeFilter) return false;
    if (!q) return true;
    const nq = normalize(q);
    return (
      normalize(c.nome).includes(nq) ||
      normalize(c.cpf).includes(nq) ||
      normalize(c.telefone).includes(nq) ||
      normalize(c.funcao).includes(nq) ||
      normalize(c.community_nome).includes(nq)
    );
  });

  const stats = {
    total: items.length,
    cooperados: items.filter((c) => (c.tipo || "cooperado") === "cooperado").length,
    comunitarios: items.filter((c) => c.tipo === "comunitario").length,
    filtered: filtered.length,
  };

  return (
    <div data-testid="cooperados-page">
      <SectionTitle action={canEdit && <Btn onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} data-testid="add-cooperado-btn"><Plus className="w-4 h-4" />Novo Cooperado</Btn>}>
        Cooperados e Comunitários · <UsersIcon className="inline w-5 h-5 -mt-1 text-[#3D6B57]" />
      </SectionTitle>

      {/* Filtros e busca */}
      <div className="brutal-card-sm p-3 mb-4 flex flex-wrap gap-2 items-center bg-white">
        <SearchBar
          value={q}
          onChange={setQ}
          placeholder="Buscar por nome, CPF, telefone, função ou comunidade..."
          testid="cooperados-search"
        />
        <div className="flex gap-1 border-2 border-[#111]">
          <button onClick={() => setTipoFilter("all")} data-testid="coop-filter-all" className={`px-3 py-2 text-xs font-bold uppercase tracking-widest font-mono-data ${tipoFilter === "all" ? "bg-[#111] text-white" : "bg-white"}`}>Todos</button>
          <button onClick={() => setTipoFilter("cooperado")} data-testid="coop-filter-coop" className={`px-3 py-2 text-xs font-bold uppercase tracking-widest font-mono-data border-l-2 border-[#111] ${tipoFilter === "cooperado" ? "bg-[#3D6B57] text-white" : "bg-white"}`}>Cooperados</button>
          <button onClick={() => setTipoFilter("comunitario")} data-testid="coop-filter-com" className={`px-3 py-2 text-xs font-bold uppercase tracking-widest font-mono-data border-l-2 border-[#111] ${tipoFilter === "comunitario" ? "bg-[#FF4F00] text-white" : "bg-white"}`}>Comunitários</button>
        </div>
        <select value={comunidadeFilter} onChange={(e) => setComunidadeFilter(e.target.value)} className="border-2 border-[#111] p-2 text-sm bg-white font-mono-data" data-testid="coop-comunidade-filter">
          <option value="">Todas comunidades</option>
          {communities.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
        </select>
        <div className="flex items-center gap-2 text-xs font-mono-data text-[#555] px-2 ml-auto">
          <span className="uppercase tracking-widest">Exibindo</span>
          <span className="font-bold text-[#111] text-base" data-testid="coop-count">{stats.filtered}</span>
          <span className="text-[#777]">/ {stats.total}</span>
          <span className="ml-2">·</span>
          <Badge variant="green">{stats.cooperados} coop</Badge>
          <Badge variant="orange">{stats.comunitarios} com</Badge>
        </div>
      </div>

      <div className="brutal-card overflow-x-auto">
        <table className="brutal-table">
          <thead><tr><th>Nome</th><th>Tipo</th><th>Função</th><th>Comunidade</th><th>CPF</th><th>Telefone</th>{canEdit && <th className="w-24">Ações</th>}</tr></thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="text-center py-8 text-[#777] font-mono-data">Carregando...</td></tr>}
            {!loading && filtered.length === 0 && <EmptyState message={q || tipoFilter !== "all" || comunidadeFilter ? "Nenhum resultado para o filtro atual." : "Nenhum cadastro."} cols={canEdit ? 7 : 6} />}
            {filtered.map((c) => (
              <tr key={c.id} data-testid={`cooperado-row-${c.id}`}>
                <td className="font-medium">{c.nome}</td>
                <td><Badge variant={c.tipo === "cooperado" ? "green" : "orange"}>{c.tipo === "cooperado" ? "Cooperado" : "Comunitário"}</Badge></td>
                <td className="text-sm">{c.funcao ? <Badge variant="outline">{c.funcao}</Badge> : <span className="text-[#aaa]">—</span>}</td>
                <td className="text-sm">{c.community_nome || "—"}</td>
                <td className="font-mono-data text-xs">{c.cpf || "—"}</td>
                <td className="font-mono-data text-xs">
                  {c.telefone ? (
                    <div className="flex items-center gap-2">
                      <span>{c.telefone}</span>
                      <button
                        type="button"
                        onClick={() => openWhatsapp(c.telefone, `Olá ${c.nome}! COOMFLONA aqui.`)}
                        data-testid={`whatsapp-${c.id}`}
                        className="p-1 hover:bg-[#25D366] hover:text-white border border-[#111] inline-flex"
                        title="Enviar mensagem no WhatsApp"
                      >
                        <MessageCircle className="w-3 h-3" />
                      </button>
                    </div>
                  ) : "—"}
                </td>
                {canEdit && <td><div className="flex gap-1">
                  <button onClick={() => { setEditing(c); setForm({ ...empty, ...c, community_id: c.community_id || "" }); setOpen(true); }} data-testid={`edit-cooperado-${c.id}`} className="p-1.5 hover:bg-[#FF4F00] hover:text-white border border-[#111]"><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => remove(c.id)} data-testid={`delete-cooperado-${c.id}`} className="p-1.5 hover:bg-[#E60000] hover:text-white border border-[#111]"><Trash2 className="w-3.5 h-3.5" /></button>
                </div></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Editar Cooperado" : "Novo Cooperado"} size="md">
        <form onSubmit={submit} className="grid grid-cols-2 gap-4" data-testid="cooperado-form">
          <Field label="Nome *" required value={form.nome} onChange={(v) => setForm({ ...form, nome: v })} colspan />
          <Field label="CPF" value={form.cpf} onChange={(v) => setForm({ ...form, cpf: v })} />
          <Field label="Telefone" value={form.telefone} onChange={(v) => setForm({ ...form, telefone: v })} />
          <SelectField label="Tipo *" required value={form.tipo} onChange={(v) => setForm({ ...form, tipo: v })}
            options={[{value:"cooperado",label:"Cooperado"},{value:"comunitario",label:"Comunitário"}]} />
          <SelectField label="Comunidade" value={form.community_id} onChange={(v) => setForm({ ...form, community_id: v })}
            options={communities.map((c) => ({ value: c.id, label: c.nome }))} />
          <div className="col-span-2">
            <label className="block font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">Função / Setor</label>
            <input
              value={form.funcao}
              onChange={(e) => setForm({ ...form, funcao: e.target.value })}
              list="funcao-sugestoes"
              placeholder="Ex.: Motosserrista, Motorista, Diarista..."
              className="w-full border-2 border-[#111] p-2 text-sm bg-white outline-none focus:border-[#FF4F00]"
              data-testid="cooperado-funcao"
            />
            <datalist id="funcao-sugestoes">
              {FUNCAO_SUGESTOES.map((f) => <option key={f} value={f} />)}
            </datalist>
            <div className="font-mono-data text-[9px] text-[#777] mt-1 uppercase tracking-widest">Usada nos relatórios de compras e no cadastro geral</div>
          </div>
          <div className="col-span-2 flex justify-end gap-2 pt-2 border-t-2 border-[#111]">
            <Btn type="button" variant="secondary" onClick={() => setOpen(false)}>Cancelar</Btn>
            <Btn type="submit" data-testid="save-cooperado-btn">{editing ? "Salvar" : "Criar"}</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
